import React from "react";
import { Route, Routes } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { withAuthenticator } from "@aws-amplify/ui-react";
import { I18n } from "aws-amplify/utils";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import "@aws-amplify/ui-react/styles.css";
import config from "./amplifyconfiguration.json";
import signUpFormFields from "./components/auth/SignUpForm";
import HomePage from "./components/mainPage";
import Settings from "./components/settings";
import LessonsPage from "./components/lessons";
import UsersPage from "./components/users";
import UserLessonsPage from "./components/users/UserLessonsPage";
import AddLessonPage from "./components/lessons/add";
import UpdateLessonPage from "./components/lessons/update";
import LessonPage from "./components/lessons/LessonPage";
import LessonsLayout from "./layouts/LessonsLayout";
import Lesson from "./components/lessons/Lesson";
import PrivateAdminRoute from "./components/routes/PrivateAdminRoute";
import { LessonProvider } from './context/LessonContext';
import { UserProvider } from './context/UserContext';

Amplify.configure(config);

// Create custom theme with burgundy color
const theme = createTheme({
  palette: {
    primary: {
      main: '#800020', // Burgundy color
      light: '#a52a2a',
      dark: '#5c0016',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#6b2c91', // Purple that complements burgundy
      light: '#8e44ad',
      dark: '#4a1e62',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#d4851a', // Warm amber that works with burgundy
      light: '#f39c12',
      dark: '#b7711a',
      contrastText: '#ffffff',
    },
    info: {
      main: '#2980b9', // Blue that complements the burgundy/purple palette
      light: '#3498db',
      dark: '#1f618d',
      contrastText: '#ffffff',
    },
    success: {
      main: '#27ae60', // Green that works well with burgundy
      light: '#2ecc71',
      dark: '#1e8449',
      contrastText: '#ffffff',
    },
    error: {
      main: '#c0392b', // Red that harmonizes with burgundy
      light: '#e74c3c',
      dark: '#922b21',
      contrastText: '#ffffff',
    },
  },
});

// Configure Spanish translations
I18n.putVocabularies({
  es: {
    'Sign in': 'Iniciar sesión',
    'Create Account': 'Crear cuenta',
    'Sign In': 'Iniciar sesión',
    'Sign Up': 'Registrarse',
    'Sign Out': 'Cerrar sesión',
    'Forgot your password?': '¿Olvidaste tu contraseña?',
    'Reset password': 'Restablecer contraseña',
    'Back to Sign In': 'Volver a iniciar sesión',
    'Send code': 'Enviar código',
    'Confirm': 'Confirmar',
    'Email': 'Correo electrónico',
    'Password': 'Contraseña',
    'Enter your Email': 'Ingresa tu correo electrónico',
    'Enter your Password': 'Ingresa tu contraseña',
    'Your passwords must match': 'Las contraseñas deben coincidir',
    'Password must have at least 8 characters': 'La contraseña debe tener al menos 8 caracteres',
    'Password must have uppercase letters': 'La contraseña debe tener letras mayúsculas',
    'Password must have lowercase letters': 'La contraseña debe tener letras minúsculas',
    'Password must have numbers': 'La contraseña debe tener números',
    'Password must have special characters': 'La contraseña debe tener caracteres especiales',
    'Incorrect username or password.': 'Nombre de usuario o contraseña incorrectos.',
    'User does not exist.': 'El usuario no existe.',
    'User already exists': 'El usuario ya existe',
    'Invalid verification code provided, please try again.': 'Código de verificación inválido, por favor intenta de nuevo.',
    'Invalid password format': 'Formato de contraseña inválido',
    'Account recovery requires verified contact information': 'La recuperación de cuenta requiere información de contacto verificada',
        'Username cannot be empty': 'El nombre de usuario no puede estar vacío',
    'Password attempts exceeded': 'Intentos de contraseña excedidos',
    // Navigation and UI elements
    'Settings': 'Configuración',
    'Home': 'Inicio',
    'Back': 'Atrás',
    'Save': 'Guardar',
    'Edit': 'Editar',
    'Delete': 'Eliminar',
    'Add': 'Agregar',
    'Close': 'Cerrar',
    'Search': 'Buscar',
    'Filter': 'Filtrar',
    'Sort': 'Ordenar',
    'View': 'Ver',
    'Download': 'Descargar',
    'Upload': 'Subir',
    'Print': 'Imprimir',
    'Help': 'Ayuda',
    'About': 'Acerca de',
    'Contact': 'Contacto',
    'Privacy': 'Privacidad',
    'Terms': 'Términos',
    'Menu': 'Menú',
    // Error messages
    'SignUp is not permitted for this user pool': 'El registro no está permitido para este grupo de usuarios',
    'User pool does not allow sign up': 'El grupo de usuarios no permite registros',
    'An account with the given email already exists.': 'Ya existe una cuenta con este correo electrónico.',
    'Username/client id combination not found.': 'Combinación de usuario/ID de cliente no encontrada.',
    'Password reset required for the user': 'Se requiere restablecer la contraseña para este usuario',
    'Temporary password has expired and must be reset by an administrator': 'La contraseña temporal ha expirado y debe ser restablecida por un administrador',
    'Access denied': 'Acceso denegado',
    'Invalid session for the user, session is expired': 'Sesión inválida para el usuario, la sesión ha expirado',
    // Email verification flow
    'We Emailed You': 'Te hemos enviado un correo de verificación desde: no-reply@verificationemail.com',
    'Tu código está en camino, hemos enviado un correo a: a***@a***. Puede tardar un minuto en llegar.': 'Tu código está en camino. Para iniciar sesión, ingresa el código que enviamos a tu correo electrónico. Puede tardar un minuto en llegar.',
    'Confirmation Code': 'Código de confirmación',
    'Enter your code': 'Ingresa tu código',
    'Resend Code': 'Reenviar código',
    'We sent a verification code to': 'Enviamos un código de verificación a',
    'It may take a minute to arrive': 'Puede tardar un minuto en llegar',
    'Verification': 'Verificación',
    'Please check your email for the verification code': 'Por favor verifica tu correo electrónico para el código de verificación',
    'Code': 'Código',
    'Submit': 'Enviar',
    'Skip': 'Omitir',
    'Verify': 'Verificar',
    // Additional common messages
    'Loading...': 'Cargando...',
    'Please try again': 'Por favor intenta de nuevo',
    'Something went wrong': 'Algo salió mal',
    'Network error': 'Error de red',
    'Try again': 'Inténtalo de nuevo',
    'Cancel': 'Cancelar',
    'Continue': 'Continuar',
    'Next': 'Siguiente',
    'Previous': 'Anterior',
    'Finish': 'Finalizar'
  }
});

I18n.setLanguage('es');

function App() {
  return (
    <ThemeProvider theme={theme}>
      <UserProvider>
        <LessonProvider>
          <Routes>
          <Route path="/" element={<HomePage />} />
          <Route 
            path="/settings" 
            element={
              <PrivateAdminRoute>
                <Settings />
              </PrivateAdminRoute>
            } 
          />
          <Route 
            path="/settings/lessons" 
            element={
              <PrivateAdminRoute>
                <LessonsPage />
              </PrivateAdminRoute>
            } 
          />
          <Route 
            path="/settings/users" 
            element={
              <PrivateAdminRoute>
                <UsersPage />
              </PrivateAdminRoute>
            } 
          />
          <Route 
            path="/settings/userlessons" 
            element={
              <PrivateAdminRoute>
                <UserLessonsPage />
              </PrivateAdminRoute>
            } 
          />
          <Route 
            path="/settings/lessons/new" 
            element={
              <PrivateAdminRoute>
                <AddLessonPage />
              </PrivateAdminRoute>
            } 
          />
          <Route 
            path="/settings/lesson/:id" 
            element={
              <PrivateAdminRoute>
                <UpdateLessonPage />
              </PrivateAdminRoute>
            } 
          />
          <Route path="/lesson/:id" element={<LessonPage />} />
          <Route path="/lecciones" element={<LessonsLayout />}>
            <Route index element={<Lesson />} />
            <Route path="lesson/:id" element={<Lesson />} />
          </Route>
        </Routes>
      </LessonProvider>
    </UserProvider>
    </ThemeProvider>
  );
}

export default withAuthenticator(App, {
  formFields: signUpFormFields,
});
